import React, { useEffect, useRef, useState } from 'react';
import { VIDEOS, type VideoId } from '../content/videos';
import usePageLoaded from '../hooks/usePageLoaded';
import usePageShown from '../hooks/usePageShown';
import useReducedMotion from '../hooks/useReducedMotion';

/**
 * What the stage needs to know to show Pause/Play: 'unavailable' when there
 * will be no film (reduced motion, data saver, an error or a refused autoplay);
 * 'off' while it has not played yet; then 'playing' or 'paused'. 'paused' also
 * covers an off-screen film and a browser-initiated pause, not only the visitor's.
 */
export type HeroVideoState = 'unavailable' | 'off' | 'playing' | 'paused';

// The hero is 100svh, so this is exactly when the 2:3 portrait film covers by height like the still and the two line up; chosen once at mount (rotation keeps it).
const PORTRAIT_QUERY = '(max-aspect-ratio: 2/3)';

interface HeroVideoProps {
  video: VideoId;
  /** CSS object-position, the same focal point as the still beneath. */
  focal?: string;
  /** The visitor pressed Pause. */
  paused: boolean;
  onState?: (state: HeroVideoState) => void;
}

const saveData = () =>
  typeof navigator !== 'undefined' &&
  (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;

const uprightPhone = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PORTRAIT_QUERY).matches;

/**
 * A muted, looping film laid over a hero layer's still. The loop's soft fade
 * is in the file (scripts/build-marketing-video.mjs), so this only decides
 * whether to play: never under reduced motion or data saver, never off
 * screen, never once the visitor has paused it. It stays transparent until
 * it has actually played, so a refused autoplay or a decode error simply
 * leaves the still, which is the film's first frame.
 */
export default function HeroVideo({ video, focal, paused, onState }: HeroVideoProps) {
  const reduced = useReducedMotion();
  const loaded = usePageLoaded();
  const pageShown = usePageShown();
  const ref = useRef<HTMLVideoElement | null>(null);
  const [source] = useState(() => (uprightPhone() ? VIDEOS[video].portrait : VIDEOS[video].landscape));
  const [visible, setVisible] = useState(true);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const skip = reduced || saveData();

  const state: HeroVideoState = skip || failed ? 'unavailable' : !started ? 'off' : playing ? 'playing' : 'paused';
  useEffect(() => {
    onState?.(state);
  }, [state, onState]);

  // A toggle of reduced motion / data saver unmounts the film: forget that it played.
  useEffect(() => {
    if (skip) {
      setStarted(false);
      setPlaying(false);
    }
  }, [skip]);

  // Keyed on skip, which decides whether the <video> node exists at all.
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) =>
      setVisible(entries[entries.length - 1]?.isIntersecting ?? true),
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [skip]);

  // Release the decoder when the node goes away (unmount, or skip turning on).
  useEffect(() => {
    const node = ref.current;
    return () => {
      // StrictMode's effect re-run leaves the node in the document; a real unmount or `skip` removes it first.
      if (!node || node.isConnected) return;
      node.pause();
      node.removeAttribute('src');
      node.load();
    };
  }, [skip]);

  useEffect(() => {
    const node = ref.current;
    if (!node || !loaded || failed) return undefined;
    if (paused || !visible || !pageShown) {
      node.pause();
      return undefined;
    }
    const attempt = node.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch((err: { name?: string }) => {
        // An AbortError only means a pause() overtook this play(); a refusal is final.
        if (err?.name === 'NotAllowedError') setFailed(true);
      });
    }
    return undefined;
  }, [loaded, paused, visible, pageShown, failed, skip]);

  if (skip) return null;
  return (
    <video
      ref={ref}
      className="mk-hero__video"
      data-testid="hero-video"
      data-shown={started && !failed ? 'true' : 'false'}
      src={loaded ? source : undefined}
      muted
      loop
      playsInline
      disablePictureInPicture
      preload="none"
      aria-hidden="true"
      style={focal ? { objectPosition: focal } : undefined}
      onPlaying={() => {
        setStarted(true);
        setPlaying(true);
      }}
      onPause={() => setPlaying(false)}
      onError={() => setFailed(true)}
    />
  );
}
